import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, List, ListItem,
    ListItemButton, ListItemText, Stack, Table, TableBody, TableCell, TableRow, Typography, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import MemoryIcon from '@mui/icons-material/Memory';
import { getItemRevisions, getLibraryItem, requestLibraryDownloads } from './apiLibrary';
import { BRAND, FILE_KIND_LABELS, LIBRARY_DISCLAIMER, formatDate, formatSize, personName, startDownloads } from './libraryConstants';
import ItemActions from './ItemActions';
import StatusChip from './StatusChip';
import VerificationChip from './VerificationChip';

const HISTORY_LABELS = {
    created: 'Created', edited: 'Edited', file_added: 'File added', file_removed: 'File removed',
    submitted: 'Submitted for review', approved: 'Approved', rejected: 'Returned to draft',
    published: 'Published', superseded: 'Superseded', withdrawn: 'Withdrawn'
};

const Section = ({ title, children }) => (
    <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.primary, mb: 1 }}>{title}</Typography>
        {children}
    </Box>
);

const LibraryItemDetail = ({ groupId, itemId, config, refreshKey, onClose, onOpenItem, onEdit, onChanged }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [item, setItem] = useState(null);
    const [revisions, setRevisions] = useState([]);
    const [error, setError] = useState('');
    const [busyFile, setBusyFile] = useState('');

    // Blank the drawer when switching items, but not on a refresh of the same one
    useEffect(() => { setItem(null); setRevisions([]); }, [itemId]);

    useEffect(() => {
        if (!itemId) return undefined;
        let cancelled = false;
        setError('');
        Promise.all([getLibraryItem(groupId, itemId), getItemRevisions(groupId, itemId)]).then(([it, revs]) => {
            if (cancelled) return;
            if (it.error) { setError(it.error === 'Not found' ? 'This device does not exist or you do not have access to it.' : it.error); return; }
            setItem(it);
            setRevisions(Array.isArray(revs) ? revs : []);
        });
        return () => { cancelled = true; };
    }, [groupId, itemId, refreshKey]);

    const label = (list, code) => {
        const found = (list || []).find(x => x.code === code);
        return found ? `${found.label} (${code})` : code;
    };

    // Downloads one attached file. The API signs every file of a kind, in the
    // same order as item.files, so pick ours by its position among that kind.
    const downloadFile = async (file) => {
        setBusyFile(file._id);
        const res = await requestLibraryDownloads(groupId, {
            itemIds: [item._id],
            kinds: [file.kind],
            includeSuperseded: item.status === 'superseded'
        });
        setBusyFile('');
        if (res.error) { setError(res.error); return; }
        const index = item.files.filter(f => f.kind === file.kind).findIndex(f => f._id === file._id);
        const signed = res.files[index];
        if (!signed) { setError('That file is not available for download.'); return; }
        startDownloads([signed]);
    };

    const canDownload = item && item.status !== 'withdrawn';
    const lastNote = (action) => item && [...(item.history || [])].reverse().find(h => h.action === action);

    return (
        <Drawer anchor="right" open={Boolean(itemId)} onClose={onClose}
            PaperProps={{ sx: { width: fullScreen ? '100%' : 480, maxWidth: '100%' } }}
            sx={{ zIndex: (t) => t.zIndex.modal }}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" color="text.secondary">Device</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND.primary, wordBreak: 'break-all' }}>
                            {item ? item.code : ' '}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
                </Stack>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {!item && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}

                {item && (
                    <>
                        {item.title && <Typography sx={{ mt: 0.5 }}>{item.title}</Typography>}
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                            <StatusChip status={item.status} />
                            <Chip size="small" variant="outlined" label={`Revision ${item.revision}`} />
                            <VerificationChip verification={item.verification} />
                        </Stack>

                        <Box sx={{ mt: 2 }}>
                            <ItemActions groupId={groupId} config={config} item={item} onChanged={onChanged} onEdit={onEdit} />
                        </Box>

                        {item.status === 'withdrawn' && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                This device was withdrawn{lastNote('withdrawn') && lastNote('withdrawn').note ? `: ${lastNote('withdrawn').note}` : '.'} Its files can no longer be downloaded.
                            </Alert>
                        )}
                        {item.status === 'superseded' && (
                            <Alert severity="info" sx={{ mt: 2 }}
                                action={item.supersededBy && <Button color="inherit" size="small" onClick={() => onOpenItem(item.supersededBy)}>View current</Button>}>
                                A newer revision replaces this one.
                            </Alert>
                        )}
                        {item.status === 'draft' && lastNote('rejected') && (
                            <Alert severity="warning" sx={{ mt: 2 }}>Returned by the reviewer: {lastNote('rejected').note}</Alert>
                        )}

                        <Box sx={{
                            mt: 2, borderRadius: 2, bgcolor: 'grey.100', height: 220,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                        }}>
                            {item.previewUrl
                                ? <img src={item.previewUrl} alt={`${item.code} preview`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                : <Stack alignItems="center" spacing={1}><MemoryIcon sx={{ fontSize: 48, color: 'grey.400' }} /><Typography variant="body2" color="text.secondary">No preview image</Typography></Stack>}
                        </Box>

                        <Section title="Details">
                            <Table size="small" sx={{ '& td': { px: 0, borderBottom: '1px solid', borderColor: 'divider' }, '& td:first-of-type': { color: 'text.secondary', width: 130 } }}>
                                <TableBody>
                                    <TableRow><TableCell>Device type</TableCell><TableCell>{label(config.taxonomy.deviceTypes, item.deviceType)}</TableCell></TableRow>
                                    <TableRow><TableCell>Manufacturer</TableCell><TableCell>{label(config.taxonomy.manufacturers, item.manufacturer)}</TableCell></TableRow>
                                    <TableRow><TableCell>Model</TableCell><TableCell>{item.model}</TableCell></TableRow>
                                    {item.variant ? <TableRow><TableCell>Variant</TableCell><TableCell>{item.variant}</TableCell></TableRow> : null}
                                    {item.orderingCode ? <TableRow><TableCell>Ordering code</TableCell><TableCell sx={{ wordBreak: 'break-all' }}>{item.orderingCode}</TableCell></TableRow> : null}
                                    {item.publishedAt ? <TableRow><TableCell>Published</TableCell><TableCell>{formatDate(item.publishedAt)}</TableCell></TableRow> : null}
                                    {item.verificationNote ? <TableRow><TableCell>Verification note</TableCell><TableCell>{item.verificationNote}</TableCell></TableRow> : null}
                                </TableBody>
                            </Table>
                            {item.description && <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>{item.description}</Typography>}
                            {item.tags && item.tags.length > 0 && (
                                <Stack direction="row" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
                                    {item.tags.map(t => <Chip key={t} size="small" label={t} />)}
                                </Stack>
                            )}
                        </Section>

                        <Section title="Files">
                            {item.files.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No files attached yet.</Typography>
                            ) : (
                                <List dense disablePadding>
                                    {item.files.map(f => (
                                        <ListItem key={f._id} disableGutters secondaryAction={canDownload && (
                                            <IconButton edge="end" aria-label={`Download ${FILE_KIND_LABELS[f.kind]}`} onClick={() => downloadFile(f)} disabled={busyFile === f._id}>
                                                {busyFile === f._id ? <CircularProgress size={20} /> : <DownloadIcon />}
                                            </IconButton>
                                        )}>
                                            <ListItemText
                                                primary={FILE_KIND_LABELS[f.kind] || f.kind}
                                                secondary={`${f.fileType.toUpperCase()} · ${formatSize(f.fileSize)} · ${formatDate(f.uploadedAt)}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Section>

                        {revisions.length > 1 && (
                            <Section title="Revisions">
                                <List dense disablePadding>
                                    {revisions.map(r => (
                                        <ListItemButton key={r._id} selected={r._id === item._id} onClick={() => onOpenItem(r._id)} sx={{ borderRadius: 1 }}>
                                            <ListItemText primary={`Revision ${r.revision}`} secondary={formatDate(r.publishedAt || r.createdAt)} />
                                            <StatusChip status={r.status} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Section>
                        )}

                        {item.history && item.history.length > 0 && (
                            <Section title="History">
                                <List dense disablePadding>
                                    {[...item.history].reverse().map((h, i) => (
                                        <ListItem key={i} disableGutters alignItems="flex-start">
                                            <ListItemText
                                                primary={`${HISTORY_LABELS[h.action] || h.action}${personName(h.by) ? ` by ${personName(h.by)}` : ''}`}
                                                secondary={<>{formatDate(h.at)}{h.note ? <><br />{h.note}</> : null}</>}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Section>
                        )}

                        <Divider sx={{ mt: 3, mb: 1.5 }} />
                        <Typography variant="caption" color="text.secondary">{LIBRARY_DISCLAIMER}</Typography>
                    </>
                )}
            </Box>
        </Drawer>
    );
};

export default LibraryItemDetail;
